/**
 * The project's knowledge (EVOLV-308/332): `/api/v1/assistant/knowledge/…`.
 *
 * Entries (rules, terms, metrics, caveats, ready queries), uploaded documents
 * and the hybrid search behind the assistant. Every read is the tenant's own
 * data: no cache once nothing observes it (`gcTime: 0`). The backend answers
 * 404 wherever the assistant is off (see `api/assistant.js`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import useAuthStore, { selectPrincipal } from 'stores/authStore';

const BASE = '/api/v1/assistant/knowledge';

/** One slug or id as one path segment (never `/`, `?`, `#` or a dot segment). */
const seg = (id) => {
  const value = String(id);
  return /^\.*$/.test(value) ? '-' : encodeURIComponent(value);
};

export const knowledgeKeys = {
  all: ['assistant', 'knowledge'],
  document: (id, offset) => ['assistant', 'knowledge', 'document', id, offset],
};

const retryTransient = (count, err) => {
  const status = err?.response?.status;
  if (status && status < 500) return false;
  return count < 1;
};

// While a document is being indexed the list is polled this often.
export const INDEXING_POLL_MS = 2000;

export function isIndexing(data) {
  return Array.isArray(data?.documents) && data.documents.some((d) => d.status === 'indexing');
}

/**
 * `{published, drafts, can_write, documents, embeddings}`; polls while a
 * document is being indexed.
 */
export function useKnowledge({ enabled = true } = {}) {
  return useQuery({
    queryKey: knowledgeKeys.all,
    queryFn: () => apiClient.get(`${BASE}/`).then((r) => r.data),
    enabled,
    retry: retryTransient,
    refetchInterval: (query) => (isIndexing(query.state.data) ? INDEXING_POLL_MS : false),
    staleTime: 0,
    gcTime: 0,
  });
}

/** The knowledge endpoints answer with the whole payload: keep it. */
function useKnowledgeMutation(mutationFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: () => ({ principal: selectPrincipal(useAuthStore.getState()) }),
    onSuccess: (data, _variables, context) => {
      // Clearing the query cache does not cancel an in-flight mutation.
      if (context?.principal !== selectPrincipal(useAuthStore.getState())) return;
      if (data && Array.isArray(data.published)) qc.setQueryData(knowledgeKeys.all, data);
      else qc.invalidateQueries({ queryKey: knowledgeKeys.all });
    },
  });
}

/** PUT an entry's draft: `{kind, slug, title, body, query?, publish}`. */
export function useSaveEntry() {
  return useKnowledgeMutation(({ slug, ...body }) =>
    apiClient.put(`${BASE}/entries/${seg(slug)}/`, body).then((r) => r.data),
  );
}

/** Drop the caller's draft of `slug`; with `published`, retire the published entry too. */
export function useRemoveEntry() {
  return useKnowledgeMutation(({ slug, published }) =>
    apiClient
      .delete(`${BASE}/entries/${seg(slug)}/`, { params: published ? { published: 1 } : {} })
      .then((r) => r.data),
  );
}

export function usePublishEntries() {
  return useKnowledgeMutation((slugs) =>
    apiClient.post(`${BASE}/publish/`, { slugs }).then((r) => r.data),
  );
}

/** Upload a text file (`.txt`, `.md`, …) with an optional title. */
export function useUploadDocument() {
  return useKnowledgeMutation(({ file, title }) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return apiClient
      .post(`${BASE}/documents/`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  });
}

export function useDeleteDocument() {
  return useKnowledgeMutation((id) => apiClient.delete(`${BASE}/documents/${seg(id)}/`));
}

export function useReindexDocument() {
  return useKnowledgeMutation((id) =>
    apiClient.post(`${BASE}/documents/${seg(id)}/`, {}).then((r) => r.data),
  );
}

/** Read one bounded page of a visible document; drafts are private to their author. */
export function useKnowledgeDocument(id, { offset = 0, enabled = true } = {}) {
  return useQuery({
    queryKey: knowledgeKeys.document(id, offset),
    queryFn: () => apiClient.get(`${BASE}/documents/${seg(id)}/`, { params: { offset, limit: 6000 } }).then((r) => r.data),
    enabled: enabled && Boolean(id),
    retry: retryTransient,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Explicitly publish the reviewed draft and start its indexing. */
export function usePublishDocument() {
  return useKnowledgeMutation((id) =>
    apiClient.post(`${BASE}/documents/${seg(id)}/`, { action: 'publish' }).then((r) => r.data),
  );
}

/** The hybrid search as the assistant sees it: `{query, hits}`. */
export function searchKnowledge(q) {
  return apiClient.get(`${BASE}/search/`, { params: { q } }).then((r) => r.data);
}

export function useKnowledgeSearch() {
  return useMutation({ mutationFn: searchKnowledge });
}
